import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner, Alert } from "react-bootstrap";
import toastStore from "../../store/ToastStore";
import { testCloudConnection, saveCloudCredentials, clearCloudCredentials, getCloudStatus } from "../../api/cloudApi";

function extractErrorMessage(e, fallback) {
  return e?.response?.data?.message || e?.message || fallback;
}

export default function CloudApiSettingsModal({ server, show, onClose }) {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState("");
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!server?.id) return;
    setLoading(true);
    setError(null);
    getCloudStatus(server.id)
      .then((data) => {
        setStatus(data);
        setOrganizationId(data?.organizationId || "");
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [server?.id]);

  useEffect(() => {
    if (show) {
      load();
      setApiKey("");
      setOrganizations([]);
    }
  }, [show, load]);

  const credentialsEntered = apiKey.trim().length > 0;

  const handleTest = async () => {
    if (!credentialsEntered) {
      toastStore.error("Заполните apiKey.");
      return;
    }
    setTesting(true);
    try {
      const { organizations: orgs } = await testCloudConnection({ apiKey: apiKey.trim() });
      setOrganizations(orgs || []);
      if (orgs?.length === 1) setOrganizationId(orgs[0].id);
      toastStore.success(`Подключение работает. Организаций доступно: ${orgs?.length ?? 0}.`);
    } catch (e) {
      toastStore.error(extractErrorMessage(e, "Не удалось подключиться к iikoCloud."));
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!credentialsEntered) {
      toastStore.error("Заполните apiKey.");
      return;
    }
    setSaving(true);
    try {
      await saveCloudCredentials(server.id, {
        apiKey: apiKey.trim(),
        organizationId: organizationId || undefined,
      });
      toastStore.success("iikoCloud подключён.");
      onClose();
    } catch (e) {
      const orgs = e?.response?.data?.organizations;
      if (orgs?.length) {
        setOrganizations(orgs);
        toastStore.error("Для этого ключа несколько организаций — выберите нужную и сохраните ещё раз.");
      } else {
        toastStore.error(extractErrorMessage(e, "Не удалось сохранить подключение."));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setClearing(true);
    try {
      await clearCloudCredentials(server.id);
      toastStore.success("iikoCloud отключён.");
      load();
    } catch (e) {
      toastStore.error("Не удалось отключить iikoCloud.");
    } finally {
      setClearing(false);
    }
  };

  const busy = testing || saving || clearing;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>iikoCloud API — {server?.name || server?.server}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-body-secondary py-3">
            <Spinner animation="border" size="sm" />
            Загрузка…
          </div>
        ) : (
          <Form>
            {error && <Alert variant="danger">Не удалось загрузить статус подключения.</Alert>}

            {status?.configured ? (
              <Alert variant="success" className="py-2">
                Подключено.
                {status.organizationId && (
                  <>
                    <br />Организация: <code>{status.organizationId}</code>
                  </>
                )}
              </Alert>
            ) : (
              <Alert variant="secondary" className="py-2">
                iikoCloud не подключён для этого сервера. Подключение необязательно — без него AI-помощник
                просто не сможет отвечать на вопросы про остатки на складе.
              </Alert>
            )}

            <Form.Text className="d-block mb-2 text-body-secondary">
              apiKey выдаётся в iikoWeb → «Интеграции» → «API-ключи».
              {status?.configured && " Оставьте поле пустым, чтобы не менять уже сохранённое значение."}
            </Form.Text>

            <Form.Group className="mb-3">
              <Form.Label>apiKey</Form.Label>
              <Form.Control
                type="password"
                placeholder={status?.configured ? "•••••••• (сохранён)" : "apiKey из iikoWeb"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
              />
            </Form.Group>

            {organizations.length > 1 && (
              <Form.Group className="mb-2">
                <Form.Label>Организация</Form.Label>
                <Form.Select value={organizationId} onChange={(e) => setOrganizationId(e.target.value)}>
                  <option value="">Выберите организацию</option>
                  {organizations.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            <Button variant="outline-secondary" size="sm" onClick={handleTest} disabled={busy || !credentialsEntered}>
              {testing ? <Spinner animation="border" size="sm" /> : "Проверить подключение"}
            </Button>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        {status?.configured && (
          <Button variant="outline-danger" onClick={handleClear} disabled={busy || loading} className="me-auto">
            {clearing ? <Spinner animation="border" size="sm" /> : "Отключить"}
          </Button>
        )}
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Закрыть
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={busy || loading || !credentialsEntered}>
          {saving ? <Spinner animation="border" size="sm" /> : "Сохранить"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
